import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type ColumnDef } from '@tanstack/react-table';
import { useRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PatientSurveyHistoryItem } from 'types/surveys';
import { formatUnifiedForDisplay } from 'utils/date';
import { TestDetailExpanded } from '../TestDetailExpanded';
import { useTestsPatientHistory } from './useTestsPatientHistory';

export const TestsSection = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.PSYCHOLOGIST);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  const handleViewTest = (testId: string): void => {
    setSelectedTestId(prev => (prev === testId ? null : testId));
  };

  useEffect(() => {
    if (selectedTestId) {
      detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedTestId]);

  const columns: ColumnDef<PatientSurveyHistoryItem>[] = useMemo(
    () => [
      {
        id: 'name',
        accessorFn: row => row.survey.name,
        header: t('PsychologistPatients.PatientProfile.Tests.name.label'),
      },
      {
        id: 'completedAt',
        accessorKey: 'completedAt',
        header: t('PsychologistPatients.PatientProfile.Tests.completedAt.label'),
        cell: ({ row }) =>
          row.original.completedAt
            ? formatUnifiedForDisplay(row.original.completedAt, 'dd/MM/yyyy')
            : '-',
      },
      {
        id: 'score',
        accessorKey: 'totalSurveyScore',
        header: t('PsychologistPatients.PatientProfile.Tests.score.label'),
        cell: ({ row }): number | string => {
          const score = row.original.totalSurveyScore;
          return score ?? '-';
        },
      },
      {
        id: 'actions',
        header: t('PsychologistPatients.PatientProfile.Tests.actions.label'),
        cell: ({ row }): React.ReactElement => {
          const isSelected = selectedTestId === row.original.id;
          return (
            <Button
              variant={isSelected ? 'secondary' : 'terciary'}
              className="text-xs w-24"
              onClick={() => handleViewTest(row.original.id)}
              type="button"
            >
              {isSelected
                ? t('PsychologistPatients.PatientProfile.Tests.hide')
                : t('PsychologistPatients.PatientProfile.Tests.view')}
            </Button>
          );
        },
      },
    ],
    [t, selectedTestId]
  );

  const tableState = useTestsPatientHistory(columns);

  if (!tableState) {
    return null;
  }

  const { table, tests, isLoading } = tableState;

  const selectedTest = tests.find(test => test.id === selectedTestId) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <PaginatedTable<PatientSurveyHistoryItem>
        table={table}
        columns={columns}
        isLoading={isLoading}
        emptyTitle={t('PsychologistPatients.PatientProfile.Tests.empty')}
        title={t('PsychologistPatients.PatientProfile.Tests.heading')}
        withSearch
      />
      {selectedTest && (
        <div ref={detailsRef}>
          <Card
            className="bg-white gap-3"
            title={t('PsychologistPatients.PatientProfile.Tests.heading')}
          >
            <TestDetailExpanded test={selectedTest} />
          </Card>
        </div>
      )}
    </div>
  );
};
